import React, { useContext, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ChatWizardHeader from './ChatWizardHeader';
import ContentBox from './ContentBox';
import ActionList from './ActionList';
import ActionListItem from './ActionListItem';
import i18n from '../i18n';
import AppContext from '../contexts/AppContext';
import { getDurationTitle } from '../utils/timeUtils';
import { getActiveContractsFromUser, getContractById } from '../utils/contractUtils';
import { getBrandPrebookingDurations } from '../utils/durationOptions';

const StyledContainer = styled.div`
  flex: 1;
  display: flex;
  overflow: hidden;
  flex-direction: column;
`;

const DEFAULT_MAX_DURATION = 24 * 60 * 60 * 60;

const firstDefined = (...values) => values.find(v => v !== null && v !== undefined);
// For max-duration fallbacks, also skip an explicit 0 — semantically that means
// "no upper limit", so we want to fall through to the next candidate / default
// rather than treating it as a hard cap of 0 minutes.
const firstPositive = (...values) => values.find(v => typeof v === 'number' && v > 0);

const PrebookingDurationPage = props => {
  const context = useContext(AppContext);
  const activeContracts = useMemo(() => getActiveContractsFromUser(context.user), [context.user]);
  const selectedContract = useMemo(() => {
    const selected = getContractById(activeContracts, props.selectedContractId);
    return selected?.raw || activeContracts[0]?.raw || context.user?.contract;
  }, [activeContracts, props.selectedContractId, context.user]);

  // Duration priority: Contract overrides UserGroup (pricing model). UserGroup is used
  // only when the contract does not define the limit (TULQA-585). Fallback order for
  // on-site is: contract on-site -> contract general -> userGroup on-site -> userGroup
  // general. Recomputed when presence, contract, or userGroup changes to avoid stale
  // values. Semantics for an explicit 0 differ by end:
  //   - min: 0 means "no minimum required" -> minDuration becomes 0.
  //   - max: 0 means "no upper limit"       -> fall through to the next candidate /
  //     default rather than capping at 0 minutes.
  const contract = selectedContract;
  const userGroup = context.user?.userGroup;

  const minDuration = useMemo(() => {
    if (props.presence === 'onsite') {
      return firstDefined(
        contract?.minPbOnSiteDuration,
        contract?.minPbDuration,
        userGroup?.minOnSiteDuration,
        userGroup?.minPrebookingDuration,
      ) ?? 0;
    }
    return (contract?.minPbDuration ?? userGroup?.minPrebookingDuration) ?? 0;
  }, [props.presence, contract, userGroup]);

  const maxDuration = useMemo(() => {
    if (props.presence === 'onsite') {
      return firstPositive(
        contract?.maxPbOnSiteDuration,
        contract?.maxPbDuration,
        userGroup?.maxOnSiteDuration,
        userGroup?.maxPrebookingDuration,
      ) ?? DEFAULT_MAX_DURATION;
    }
    return firstPositive(contract?.maxPbDuration, userGroup?.maxPrebookingDuration) ?? DEFAULT_MAX_DURATION;
  }, [props.presence, contract, userGroup]);

  // Clear a stale duration selection on mount (e.g. if the parent restored a value that
  useEffect(() => {
    if (!getBrandPrebookingDurations().includes(props.duration)) {
      props.onDurationChanged(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const times = useMemo(() => {
    return getBrandPrebookingDurations().reduce((memo, minutes) => {
      const seconds = minutes * 60;
      if (seconds >= minDuration && seconds <= maxDuration) {
        return memo.concat({
          title: getDurationTitle(minutes),
          minutes
        });
      }
      return memo;
    }, []);
  }, [minDuration, maxDuration]);

  return (
    <StyledContainer>
      <ChatWizardHeader
        title={i18n('prebooking_duration_header')}
      />
      <ContentBox>
        <ActionList>
          {times.map(({ title, minutes }) => (
            <ActionListItem
              data-test={minutes + 'MinDurationChatWizard'}
              key={minutes}
              title={title}
              onClick={() => {
                props.onDurationChanged(minutes);
              }}
              selected={props.duration === minutes}
              lineHeight={30}
            />
          ))}
        </ActionList>
      </ContentBox>
    </StyledContainer>
  );
};

PrebookingDurationPage.propTypes = {
  onDurationChanged: PropTypes.func.isRequired,
  duration: PropTypes.number,
  selectedContractId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default PrebookingDurationPage;
